import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {ActivityIndicator, FlatList, NativeScrollEvent, NativeSyntheticEvent, Platform, ScrollView} from 'react-native';
import MainStyle from "../../../MainStyle";
import NewHeader from "../../components/NewHeader";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import {
  BrandName, isFullSwing, ITEM_MARGIN_HORIZONTAL, ITEM_WEIGHT, LANDING_PAGE_URL, SESSION_EXPIRE_TIME, TOTAL_ITEM_WEIGHT
} from "../../constants";
import {Tournament, User} from "../../../generated/smg_service_pb";
import {StoreContext} from "../../components/StoreContextProvider";
import useIdleLogout from "../../hooks/useIdleLogout";
import TournamentItem from "../../components/TournamentItem";
import {AntDesign} from '@expo/vector-icons';
import {colors} from "../../assets/styles/colors";
import BottomNavigation from "../../components/BottomNavigation";
import ServiceClient from "../../utils/ServiceClient";
import {CancelModalProps} from "../../types";
import {useIsFocused, useNavigation} from "@react-navigation/native";
import JoinTournamentButton from "../../components/JoinTournamentButton";
import ContinueAutoPlayModal from "../../modals/ContinueAutoPlay";
import P from "../../lib/P";
import Div from "../../lib/Div";
import Button from "../../components/Button";
import FSHeader from "../../components/FSHeader";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";

const TournamentsListingScreen = () => {
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user, session, extendedUserData} = Context;
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(1);
  const flatListRef = useRef<FlatList<Tournament>>(null);
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const isFocused = useIsFocused();
  const [isLoading, setIsLoading] = useState(false);
  const [continueAutoPlay, setContinueAutoPlay] = useState(false);

  const leaveTournament = () => {
    Context.setLoadingOverlay(true);

    ServiceClient.leaveTournament(tournaments[activeIndex - 1].id).then(() => {
      listDefaultTournaments();

    }).catch(err => {
      console.log('leaveTournament err', err);
      Context.setLoadingOverlay(false);

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const scrollToItem = (index: number) => {
    flatListRef.current?.scrollToIndex({animated: true, index: index});
  };

  const getItemLayout = (data: any, index: number) => (
    {length: TOTAL_ITEM_WEIGHT, offset: TOTAL_ITEM_WEIGHT * index, index}
  );

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const slideSize = event.nativeEvent.layoutMeasurement.width;
    setActiveIndex(Math.ceil((event.nativeEvent.contentOffset.x + slideSize / 2) / 378));
  }, []);

  const listDefaultTournaments = () => {
    ServiceClient.listDefaultTournaments().then(res => {
      Context.setLoadingOverlay(false);
      setIsLoading(false);
      setTournaments(res);
      if (extendedUserData.hasActiveTournament && !user?.autoPlayEnabled) setContinueAutoPlay(true);

    }).catch(err => {
      console.log('listDefaultTournaments err', err);
      Context.setLoadingOverlay(false);
      setIsLoading(false);

      let conf: CancelModalProps = {
        visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          if (err.code !== 20020042 && !isFullSwing) navigation.navigate({name: 'account', params: {step: 0}});
        }
      };

      Context.setCancelModalConfig(conf);
    });
  };

  const getUser = () => {
    Context.getUser().catch(err => {
      console.log('getUser err', err);
      setTimeout(() => {
        getUser();
      }, 5000);
    });
  };

  useEffect(() => {
    if (user?.kycStatus === 6) {
      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', leftBtnText: 'Cancel', rightBtnText: 'Contact Support',
        subtitle: `Your Know Your Customer information and registration information do not match. Your account with ${BrandName} has been locked. Please contact support to resolve this issue.`,
        onResume: () => {
          if (Platform.OS === 'web') {
            window.open('https://smgames.atlassian.net/servicedesk/customer/portal/3', '_self');
          }
        },
        onClose: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.navigate({name: 'account', params: {step: 0}});
        }
      });
    }
  }, [user?.kycStatus]);

  useEffect(() => {
    if (!user || !isFocused) return;

    listDefaultTournaments();

    const time = setTimeout(() => {
      getUser();
    }, 1000 * 10);

    return () => {
      clearTimeout(time);
    };
  }, [user, isFocused]);

  useIdleLogout({enabled: isFocused, onLogout: () => setIsLoading(false)});

  useLayoutEffect(() => {
    setIsLoading(true);

    if (!session || !user) {
      (async () => {
        const time = await AsyncStorage.getItem('lastActiveTime');

        if (time && moment().diff(moment(time), 'minutes') > SESSION_EXPIRE_TIME) {
          Context.logout().then(() => {
            if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
          });

        } else {
          const val = await AsyncStorage.getItem('session');
          let curUser: User | undefined;

          if (val) {
            const Session = JSON.parse(val);
            Context.setSession(Session);

            try {
              curUser = await Context.getUser(Session);

            } catch (e: any) {
              Context.setCancelModalConfig({
                visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
                onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
              });
            }
          }

          if (!val || !curUser || !curUser.userId) {
            Context.logout().then(() => {
              if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
            });

          } else console.log('user', curUser);
        }
      })();
    }
  }, []);

  return (
    <>
      {isFullSwing ? (
        <>
          <FSHeader px={20}/>

          <Div h={2} bg="#252526"/>

          <Div gap={5} py={10} px={20}>
            <Div row alignItems="center">
              <Div w={24} alignItems="center"><FontAwesome name="user-circle" size={24} color="#3399FF"/></Div>
              <P fontFamily="DavisSans-Regular" ml={6}>{user?.username}</P>
            </Div>
          </Div>

          <Div h={2} bg="#252526"/>
        </>
      ) : <NewHeader pb={11}/>}

      <ScrollView contentContainerStyle={MainStyle.flex1}>
        <Div row justifyContent="space-between" alignItems={isFullSwing ? 'center' : 'flex-end'}
             mb={isFullSwing ? 4 : 13} h={isFullSwing ? 48 : undefined}>
          <P fontFamily={isFullSwing ? 'DavisSans-Medium' : 'Barlow-Medium'} fs={isFullSwing ? 20 : 30}
             ml={isFullSwing ? 18 : 21} lh={isFullSwing ? undefined : 24}>
            Tournaments
          </P>

          <P color={isFullSwing ? '#0AF' : colors.newGreenText} tt="uppercase" fs={isFullSwing ? 14 : 12}
             fontFamily={isFullSwing ? 'DavisSans-Medium' : 'Barlow-Bold'} mr={isFullSwing ? 18 : 5}
             ta={isFullSwing ? 'right' : undefined} w={isFullSwing ? 120 : undefined}
             onPress={() => navigation.reset({index: 0, routes: [{name: 'AllTournaments', params: {onlyCompleted: true}}]})}>
            See Completed Tournaments
          </P>
        </Div>

        {!isFullSwing && (
          <Button
            text="Enter Tourney Code" w={370} h={45} borderWidth={1}
            borderColor={isFullSwing ? '#B9B9B9' : colors.settings_border} radius={isFullSwing ? 4 : undefined}
            bg={isFullSwing ? 'black' : colors.help_icon} color={colors.enterCode} fontSize={20} fontWeight="600" mb={15}
            mx="auto" onPress={() => navigation.navigate({name: 'FriendsScreen', params: {isSponsored: true}})}
          />
        )}

        {isLoading ? (
          <Div flex={1} justifyContent="center" alignItems="center">
            <ActivityIndicator color={colors.white} size="large"/>
          </Div>
        ) : !!tournaments.length ? (
          <>
            {tournaments.length > 1 ? (
              <Div position="relative">
                <FlatList
                  ref={flatListRef} data={tournaments} keyExtractor={(item) => item.id.toString()}
                  renderItem={({item, index}) => (
                    <TournamentItem
                      w={ITEM_WEIGHT} mx={ITEM_MARGIN_HORIZONTAL} item={item} getUser={getUser}
                      extendedUserData={extendedUserData} inFocus={activeIndex === index + 1}
                    />
                  )}
                  horizontal pagingEnabled showsHorizontalScrollIndicator={false} snapToAlignment="center"
                  nativeID="tournament_list" onScroll={onScroll} getItemLayout={getItemLayout}
                  style={[MainStyle.m_x_auto, MainStyle.full_w, MainStyle.max_w_430, MainStyle.m_b_14]}
                />

                {activeIndex !== 1 && (
                  <Div w={44} h={64} roundedRight={200} borderWidth={1} borderColor={colors.settings_border}
                       bg={isFullSwing ? 'rgba(51, 105, 255, 0.5)' : colors.help_icon} position="absolute" top="50%"
                       left={0} mt={-32} alignItems="center" justifyContent="center">
                    <AntDesign
                      name="left" size={20} color={colors.caret} onPress={() => scrollToItem(activeIndex - 2)}
                      style={MainStyle.m_l__10}
                    />
                  </Div>
                )}

                {activeIndex !== tournaments.length && (
                  <Div w={44} h={64} roundedLeft={200} borderWidth={1} borderColor={colors.settings_border}
                       bg={isFullSwing ? 'rgba(51, 105, 255, 0.5)' : colors.help_icon} position="absolute" top="50%"
                       right={0} mt={-32} alignItems="center" justifyContent="center">
                    <AntDesign
                      name="right" size={20} color={colors.caret} onPress={() => scrollToItem(activeIndex)}
                      style={MainStyle.m_r__10}
                    />
                  </Div>
                )}
              </Div>
            ) : (
              <TournamentItem
                w={ITEM_WEIGHT} mx="auto" item={tournaments[0]} mb={14} getUser={getUser}
                extendedUserData={extendedUserData}
              />
            )}
          </>
        ) : (
          <Div flex={1} justifyContent="center" alignItems="center">
            <P fw="500" fs={32} mb={14} ml={21}>Nothing found</P>
          </Div>
        )}
      </ScrollView>

      {(!isLoading && !!tournaments.length) && (
        <>
          <JoinTournamentButton
            onReload={listDefaultTournaments} leave={leaveTournament} hideLeaveButton
            tournament={tournaments[activeIndex - 1] || {}} isTournamentSeries={!!tournaments[activeIndex - 1].seriesId}
          />

          <P color={colors.gray} ta="center" fs={17}>Malfunction voids all pays and plays</P>
          <Div h={19}/>
        </>
      )}

      {!isFullSwing && <BottomNavigation page="tournaments"/>}

      <ContinueAutoPlayModal isOpen={continueAutoPlay} onClose={() => setContinueAutoPlay(false)} onReload={getUser}/>
    </>
  );
};

export default TournamentsListingScreen;
